<script setup>
import _ from 'lodash';
import moment from 'moment';
import { computed } from 'vue';

import { useShipmentStore } from '@/store/shipmentStore';
import { useOrderStore } from '@/store/orderStore';
import { usePeriodStore } from '@/store/periodStore';

import Dropdown from '@/components/ui/Dropdown.vue';
import DateSelect from '../DateSelect.vue';

const model = defineModel();

const shipmentStore = useShipmentStore();
const orderStore = useOrderStore();
const periodStore = usePeriodStore();

const dateMasks = { input: "MM/DD/YYYY" };

const disabledPickupDates = computed(() => {
    const pickupLocation = shipmentStore.selectedPickupLocation;
    const pickupLocationApiId = pickupLocation ? pickupLocation.api_pickup_location_id : null;
    const disabledDates = [];

    switch (pickupLocationApiId) {
        case 354:
            disabledDates.push({ repeat: { weekdays: [1, 7] } });
            break;
        case 364:
            disabledDates.push({ repeat: { weekdays: [1, 2, 3] } });
            break;
        case 373:
            disabledDates.push({ repeat: { weekdays: [3] } });
            break;
        case 377:
            disabledDates.push({ repeat: { weekdays: [2, 3, 4, 5, 6] } });
            break;
    }

    const excludedIds = [353, 365, 386];
    if (!pickupLocationApiId || excludedIds.includes(pickupLocationApiId)) {
        return disabledDates;
    }
    const currentYear = new Date().getFullYear();
    const holidays = [
        `01-01`, `01-20`, `02-17`, `05-26`, `06-19`, `07-04`, `09-01`, `10-13`, `11-11`, `11-27`, `12-25`
    ];
    holidays.forEach(dayMonth => {
        const dateStr = `${currentYear}-${dayMonth}`;
        const holidayDate = new Date(dateStr);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (holidayDate >= today) {
            disabledDates.push({ start: holidayDate, end: holidayDate });
        }
    });
    return disabledDates;
});

const pickupMinDate = computed(() => {
    let date = moment().startOf("day");
    const startDate = moment(periodStore.minPeriodStartDate).startOf("day");
    const diffDays = startDate.diff(date, "days");
    if (Math.abs(diffDays) > 7) {
        date = moment(startDate).subtract(7, "days");
    }

    return date.format();
});

function handlePickupLocation(apiId) {
    const findLocation = _.find(shipmentStore.pickup_locations, function (location) {
        return location.api_pickup_location_id === apiId;
    });

    if (findLocation) {
        shipmentStore.setPickupLocation(findLocation);
    }
}
</script>

<template>
    <div>
        <div class="mb-4">
            <span class="w-full inline-block mb-3">Pickup location:</span>
            <Dropdown 
                v-if="shipmentStore.locationsForDropdown.length"
                v-model="shipmentStore.pickup_location_api_id"
                :items="shipmentStore.locationsForDropdown"
                :placeholder="$t('order.select_loc')"
                @select="handlePickupLocation"
            />
        </div>

        <div class="mb-4">
            <h4 class="mb-2">{{ $t("purchase-rent-page.shipment.pickup.dateLabel") }}</h4>

            <DateSelect
                v-model="shipmentStore.pickup_date" 
                :masks="dateMasks"
                :disabled-dates="disabledPickupDates"
                :min-date="pickupMinDate"
            />
        </div>

        <div
            v-if="shipmentStore.selectedPickupLocation?.time_note"
            class="bg-white p-4 rounded text-sm text-gray-700 border-2 border-sky-300"
            v-html="shipmentStore.selectedPickupLocation.time_note"
        ></div>
    </div>
</template>